/**
 * Backend maintenance (Laravel `php artisan down`) answers every request with 503 and,
 * when started with `--retry=N`, a `Retry-After` header. Pure helpers only — shared by
 * the proxy, the SSR fetchers and the client countdown.
 */
import { PagesPaths } from "./pages-paths";

export const MAINTENANCE_STATUS = 503;

/** Used when the backend sends no (or an unparseable) `Retry-After`. */
export const DEFAULT_RETRY_AFTER_SECONDS = 60;

// Floor: never let a tiny value turn every open tab into a reload loop against a backend
// that is still coming up. Ceiling: a typo'd `--retry=86400` must not strand visitors on
// the maintenance page for a day after the backend is already back.
export const MIN_RETRY_AFTER_SECONDS = 15;
export const MAX_RETRY_AFTER_SECONDS = 30 * 60;

export const isMaintenanceStatus = (status?: number | string | null) =>
  Number(status) === MAINTENANCE_STATUS;

const clampRetryAfter = (seconds: number) =>
  Math.min(
    MAX_RETRY_AFTER_SECONDS,
    Math.max(MIN_RETRY_AFTER_SECONDS, Math.ceil(seconds)),
  );

/**
 * `Retry-After` is either delay-seconds (`120`) or an HTTP-date
 * (`Wed, 21 Oct 2026 07:28:00 GMT`) — RFC 9110 §10.2.3. Always returns a usable,
 * clamped number of seconds so callers never branch on "missing".
 */
export function parseRetryAfter(
  raw: string | number | null | undefined,
  now: number = Date.now(),
): number {
  if (raw === null || raw === undefined) return DEFAULT_RETRY_AFTER_SECONDS;
  const value = String(raw).trim();
  if (!value) return DEFAULT_RETRY_AFTER_SECONDS;

  if (/^\d+$/.test(value)) return clampRetryAfter(Number(value));

  // An HTTP-date always names a weekday/month. Without this, Date.parse happily reads
  // "-5" or "2026" as a year.
  const date = /[a-z]/i.test(value) ? Date.parse(value) : NaN;
  if (Number.isNaN(date)) return DEFAULT_RETRY_AFTER_SECONDS;
  return clampRetryAfter((date - now) / 1000);
}

/**
 * `Retry-After` off a failed Axios call. Read structurally rather than via
 * `isAxiosError` so the proxy, which imports this file, does not pull in axios. In the
 * browser the header is only readable because the backend lists it in
 * `Access-Control-Expose-Headers`; without that it reads as missing and callers fall
 * back to DEFAULT_RETRY_AFTER_SECONDS.
 */
export const retryAfterFromAxiosError = (error: unknown): string | undefined => {
  const value = (error as { response?: { headers?: Record<string, unknown> } } | null)
    ?.response?.headers?.["retry-after"];
  return value === undefined || value === null ? undefined : String(value);
};

/** Dispatched by BaseClass when a browser-side call hits maintenance; Providers navigates. */
export const MAINTENANCE_EVENT = "backend:maintenance";
export type MaintenanceEventDetail = { retryAfterSeconds: number };

/** Query param carrying a browser-observed Retry-After to /company-error. */
export const RETRY_AFTER_PARAM = "retry_after";

/** Seconds to wait when `error` is a maintenance 503, otherwise null. */
export const maintenanceRetryAfterFromError = (error: unknown): number | null => {
  const status = (error as { response?: { status?: unknown } } | null)?.response
    ?.status;
  return isMaintenanceStatus(status as number | undefined)
    ? parseRetryAfter(retryAfterFromAxiosError(error))
    : null;
};

/**
 * Where a failed render sends the visitor. On maintenance the Retry-After rides in the
 * URL so /company-error can show the countdown without reading it back from Redis —
 * Redis is best-effort, and when it is unreachable the page would otherwise fall back
 * to the generic "unavailable" card.
 */
export const companyErrorPath = (retryAfterSeconds?: number | null) =>
  retryAfterSeconds
    ? `${PagesPaths.companyError}?${RETRY_AFTER_PARAM}=${retryAfterSeconds}`
    : PagesPaths.companyError;

/** Whole seconds left in a maintenance window ending at `until` (epoch ms), at least 1. */
export const secondsUntil = (until: number, now: number = Date.now()) =>
  Math.max(1, Math.ceil((until - now) / 1000));

const RELOAD_JITTER_MAX_SECONDS = 10;

/**
 * Every visitor parked on the maintenance page shares the same window end, so without a
 * spread they would all auto-refresh in the same second — a thundering herd against a
 * backend that has only just come back. Adds 0–10s.
 */
export const withReloadJitter = (seconds: number, random: number = Math.random()) =>
  seconds + Math.floor(random * (RELOAD_JITTER_MAX_SECONDS + 1));
